import React from "react";
import { Sentry, sentryEnabled } from "@/lib/sentry";

type State = {
  error: Error | null;
  componentStack: string | null;
};

/**
 * Catches render/runtime errors anywhere in the tree and renders the full
 * stack trace + React component stack instead of a blank screen.
 */
export class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surfaced in the browser console for log capture / debugging tools.
    console.error(
      "[RootErrorBoundary]",
      error?.name,
      error?.message,
      "\nStack:\n",
      error?.stack,
      "\nComponent stack:",
      info?.componentStack,
    );
    if (sentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setTag("boundary", "root");
        scope.setContext("react", {
          componentStack: info?.componentStack ?? "(unavailable)",
        });
        Sentry.captureException(error);
      });
    }
    this.setState({ componentStack: info?.componentStack ?? null });
  }


  private reset = () => this.setState({ error: null, componentStack: null });

  override render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const details = [
      `${error.name}: ${error.message}`,
      "",
      error.stack ?? "(no stack)",
      "",
      "Component stack:",
      componentStack ?? "(unavailable)",
    ].join("\n");

    return (
      <div className="min-h-screen bg-background text-foreground p-6 overflow-auto">
        <div className="mx-auto max-w-4xl space-y-4">
          <h1 className="text-2xl font-bold text-destructive">
            Something crashed while rendering
          </h1>
          <p className="text-sm text-muted-foreground">
            The full stack trace and component tree are below. Copy this when
            reporting the issue.
          </p>
          <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted p-4 text-xs leading-relaxed">
            {details}
          </pre>
          <div className="flex gap-3">
            <button
              onClick={this.reset}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Try again
            </button>
            <button
              onClick={() => navigator.clipboard?.writeText(details)}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium"
            >
              Copy details
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
